/**
 * SmartLink analytics page (feature #115, DASH-03).
 *
 * Renders:
 *   - a compact query form (SmartLink ID + date range)
 *   - summary KPI strip (clicks / conversions / CR% / offer count)
 *   - per-offer table with traffic-share bar visualisation
 *
 * All state is local — TanStack Query owns the remote lifecycle, the form
 * layer is a hand-rolled reducer-free shape that mirrors the pattern used
 * by the os-targeting feature (see `apps/dash/src/features/os-targeting`).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	fetchSmartlinkSummary,
	formatCrPercent,
	formatInteger,
	formatSharePercent,
	shareBarWidth,
	smartlinkAnalyticsQueryKeys,
	summaryQuerySchema,
	type OfferSummary,
	type SummaryResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — kept inline so the file is grep-friendly for the root
// wire-up agent and future pattern scraping.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";

// ---------------------------------------------------------------------------
// Default date range — last 7 days ending today. Keeps the page usable on
// first load so the operator sees a live summary without fiddling with
// the pickers first.
// ---------------------------------------------------------------------------

export function defaultDateRange(now: Date = new Date()): {
	from: string;
	to: string;
} {
	const end = new Date(
		Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
	);
	const start = new Date(end);
	start.setUTCDate(start.getUTCDate() - 6);
	return {
		from: toIso(start),
		to: toIso(end),
	};
}

function toIso(d: Date): string {
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

// ---------------------------------------------------------------------------
// Form state shape
// ---------------------------------------------------------------------------

interface FormState {
	smartlinkId: string;
	from: string;
	to: string;
}

export interface SmartlinkAnalyticsPageProps {
	/** When provided, prefills (but does not lock) the smartlink_id field. */
	initialSmartlinkId?: string;
}

export function SmartlinkAnalyticsPage({
	initialSmartlinkId,
}: SmartlinkAnalyticsPageProps = {}) {
	const initialRange = useMemo(() => defaultDateRange(), []);
	const [form, setForm] = useState<FormState>({
		smartlinkId: initialSmartlinkId ?? "",
		from: initialRange.from,
		to: initialRange.to,
	});
	const [submitted, setSubmitted] = useState<FormState | null>(() =>
		initialSmartlinkId
			? {
					smartlinkId: initialSmartlinkId,
					from: initialRange.from,
					to: initialRange.to,
				}
			: null,
	);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const parsedSubmission = useMemo(() => {
		if (!submitted) return null;
		return summaryQuerySchema.safeParse(submitted);
	}, [submitted]);

	const queryEnabled = Boolean(parsedSubmission?.success);

	const query = useQuery<SummaryResponse | null>({
		queryKey: parsedSubmission?.success
			? smartlinkAnalyticsQueryKeys.summary(
					parsedSubmission.data.smartlinkId,
					parsedSubmission.data.from,
					parsedSubmission.data.to,
				)
			: smartlinkAnalyticsQueryKeys.all,
		queryFn: () => {
			if (!parsedSubmission?.success) {
				throw new Error("Invalid query — submit the form first.");
			}
			return fetchSmartlinkSummary({
				smartlinkId: parsedSubmission.data.smartlinkId,
				from: parsedSubmission.data.from,
				to: parsedSubmission.data.to,
			});
		},
		enabled: queryEnabled,
	});

	const summary: SummaryResponse | null = query.data ?? null;

	function updateField<K extends keyof FormState>(key: K, value: string) {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (errors[key]) {
			setErrors((prev) => {
				const next = { ...prev };
				delete next[key];
				return next;
			});
		}
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = summaryQuerySchema.safeParse(form);
		if (!parsed.success) {
			setErrors(parsed.errors);
			return;
		}
		setErrors({});
		setSubmitted({ ...form });
	}

	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />
			<QueryForm
				value={form}
				errors={errors}
				loading={query.isFetching}
				onChange={updateField}
				onSubmit={handleSubmit}
			/>
			<SummarySection
				summary={summary}
				isLoading={queryEnabled && query.isLoading}
				isError={query.isError}
				errorMessage={
					query.error instanceof Error ? query.error.message : undefined
				}
				hasSubmission={Boolean(submitted)}
			/>
		</main>
	);
}

// ---------------------------------------------------------------------------
// Breadcrumb + heading — pure presentation, matches os-targeting chrome.
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<a
						href="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</a>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">SmartLinks</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Analytics</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				SmartLinks
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				SmartLink analytics
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Inspect per-offer clicks, conversions, and traffic share for a
				SmartLink within a date range. Numbers are sourced live from the
				attribution edge.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Query form — smartlink id + from / to pickers.
// ---------------------------------------------------------------------------

function QueryForm(props: {
	value: FormState;
	errors: Record<string, string>;
	loading: boolean;
	onChange: <K extends keyof FormState>(key: K, value: string) => void;
	onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
	return (
		<form
			onSubmit={props.onSubmit}
			className={`${CARD} flex flex-col gap-5 p-6`}
			noValidate
			aria-labelledby="smartlink-analytics-form-title"
		>
			<div className="flex flex-col gap-1">
				<h2
					id="smartlink-analytics-form-title"
					className="text-base font-semibold text-zinc-900"
				>
					Query
				</h2>
				<p className={HINT}>
					Enter a SmartLink ID and a reporting window.
				</p>
			</div>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-[1.5fr_1fr_1fr]">
				<Field
					id="smartlink-id"
					label="SmartLink ID"
					error={props.errors.smartlinkId}
					hint="Identifier of the SmartLink you want to report on."
				>
					<input
						id="smartlink-id"
						name="smartlinkId"
						type="text"
						autoComplete="off"
						placeholder="e.g. sl_weekend_ab"
						className={INPUT}
						value={props.value.smartlinkId}
						onChange={(e) =>
							props.onChange("smartlinkId", e.currentTarget.value)
						}
						aria-invalid={Boolean(props.errors.smartlinkId)}
					/>
				</Field>

				<Field
					id="smartlink-analytics-from"
					label="From"
					error={props.errors.from}
					hint="Start date (UTC), inclusive."
				>
					<input
						id="smartlink-analytics-from"
						name="from"
						type="date"
						className={INPUT}
						value={props.value.from}
						onChange={(e) => props.onChange("from", e.currentTarget.value)}
						aria-invalid={Boolean(props.errors.from)}
					/>
				</Field>

				<Field
					id="smartlink-analytics-to"
					label="To"
					error={props.errors.to}
					hint="End date (UTC), inclusive."
				>
					<input
						id="smartlink-analytics-to"
						name="to"
						type="date"
						className={INPUT}
						value={props.value.to}
						onChange={(e) => props.onChange("to", e.currentTarget.value)}
						aria-invalid={Boolean(props.errors.to)}
					/>
				</Field>
			</div>

			<div className="flex items-center justify-between border-t border-zinc-200 pt-4">
				<p className={HINT} aria-live="polite">
					{props.loading ? "Loading analytics." : "Ready."}
				</p>
				<button
					type="submit"
					disabled={props.loading}
					className="rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Run report
				</button>
			</div>
		</form>
	);
}

function Field(props: {
	id: string;
	label: string;
	error?: string;
	hint?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor={props.id} className={LABEL}>
				{props.label}
			</label>
			{props.children}
			{props.error ? (
				<p className={ERROR_TEXT} role="alert">
					{props.error}
				</p>
			) : props.hint ? (
				<p className={HINT}>{props.hint}</p>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Summary rendering — KPI strip, per-offer table, and empty / error states.
// ---------------------------------------------------------------------------

function SummarySection(props: {
	summary: SummaryResponse | null;
	isLoading: boolean;
	isError: boolean;
	errorMessage?: string;
	hasSubmission: boolean;
}) {
	if (!props.hasSubmission) {
		return (
			<section aria-label="Summary" className={`${CARD} p-6`}>
				<p className={HINT}>Run a report to see per-offer analytics.</p>
			</section>
		);
	}

	if (props.isLoading) {
		return (
			<section aria-label="Summary" className={`${CARD} p-6`}>
				<p className={HINT} aria-busy="true">
					Loading analytics.
				</p>
			</section>
		);
	}

	if (props.isError) {
		return (
			<section
				aria-label="Summary"
				className={`${CARD} border-red-200 bg-red-50 p-6`}
			>
				<p className="text-sm text-red-700" role="alert">
					{props.errorMessage ?? "Unable to load analytics."}
				</p>
			</section>
		);
	}

	if (!props.summary || props.summary.offers.length === 0) {
		return (
			<section aria-label="Summary" className={`${CARD} p-6`}>
				<p className={HINT}>
					No events recorded for this SmartLink in the selected window.
				</p>
			</section>
		);
	}

	const { summary } = props;
	const crBps =
		summary.total_clicks > 0
			? Math.floor((summary.total_conversions / summary.total_clicks) * 10000)
			: 0;

	return (
		<div className="flex flex-col gap-6">
			<KpiStrip
				clicks={summary.total_clicks}
				conversions={summary.total_conversions}
				crBps={crBps}
				offerCount={summary.offers.length}
			/>
			<OfferTable offers={summary.offers} />
		</div>
	);
}

function KpiStrip(props: {
	clicks: number;
	conversions: number;
	crBps: number;
	offerCount: number;
}) {
	return (
		<section
			aria-label="Summary KPIs"
			className="grid grid-cols-2 gap-3 sm:grid-cols-4"
		>
			<Kpi label="Clicks" value={formatInteger(props.clicks)} />
			<Kpi label="Conversions" value={formatInteger(props.conversions)} />
			<Kpi label="CR" value={formatCrPercent(props.crBps)} />
			<Kpi label="Offers" value={formatInteger(props.offerCount)} />
		</section>
	);
}

function Kpi(props: { label: string; value: string }) {
	return (
		<div className={`${CARD} flex flex-col gap-1 p-4`}>
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				{props.label}
			</p>
			<p className="text-2xl font-semibold text-zinc-900">{props.value}</p>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Per-offer table with inline traffic-share bar.
// ---------------------------------------------------------------------------

export function OfferTable(props: { offers: OfferSummary[] }) {
	return (
		<section
			aria-label="Per-offer analytics"
			className={`${CARD} overflow-hidden`}
		>
			<table className="w-full border-collapse text-sm">
				<thead className="bg-zinc-50 text-left">
					<tr>
						<Th>Offer</Th>
						<Th className="text-right">Clicks</Th>
						<Th className="text-right">Conversions</Th>
						<Th className="text-right">CR</Th>
						<Th>Traffic share</Th>
					</tr>
				</thead>
				<tbody>
					{props.offers.map((offer) => (
						<OfferRow key={offer.offer_id} offer={offer} />
					))}
				</tbody>
			</table>
		</section>
	);
}

function Th(props: { children: React.ReactNode; className?: string }) {
	return (
		<th
			scope="col"
			className={`border-b border-zinc-200 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500 ${
				props.className ?? ""
			}`}
		>
			{props.children}
		</th>
	);
}

function OfferRow(props: { offer: OfferSummary }) {
	const { offer } = props;
	return (
		<tr
			data-testid="offer-row"
			data-offer-id={offer.offer_id}
			className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
		>
			<td className="px-4 py-3 font-mono text-xs text-zinc-800">
				{offer.offer_id}
			</td>
			<td className="px-4 py-3 text-right tabular-nums text-zinc-800">
				{formatInteger(offer.clicks)}
			</td>
			<td className="px-4 py-3 text-right tabular-nums text-zinc-800">
				{formatInteger(offer.conversions)}
			</td>
			<td
				className="px-4 py-3 text-right tabular-nums text-zinc-800"
				data-testid="offer-cr"
			>
				{formatCrPercent(offer.cr_bps)}
			</td>
			<td className="px-4 py-3" data-testid="offer-share-cell">
				<ShareBar bps={offer.traffic_share_bps} />
			</td>
		</tr>
	);
}

function ShareBar(props: { bps: number }) {
	return (
		<div className="flex items-center gap-3">
			<div
				aria-hidden="true"
				className="relative h-2 w-40 overflow-hidden rounded-[6px] bg-zinc-100"
			>
				<div
					className="h-full rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500"
					style={{ width: shareBarWidth(props.bps) }}
				/>
			</div>
			<span className="tabular-nums text-xs text-zinc-600">
				{formatSharePercent(props.bps)}
			</span>
		</div>
	);
}

export default SmartlinkAnalyticsPage;
